import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { EditorState, NodeSelection } from 'prosemirror-state';

import type { PMDispatch } from '../editor/types';

export type HtmlClassAttrs = {
  'html-class': string;
};

export class HtmlClassExtension {
  public static readonly attrs = {
    'html-class': { default: '' },
  };

  public static getCurrentHtmlClass(state: EditorState): string {
    const { selection } = state;

    if (selection instanceof NodeSelection && selection.node.attrs?.['html-class'] !== undefined) {
      return selection.node.attrs['html-class'];
    }

    const $from = selection.$from;
    for (let depth = $from.depth; depth >= 0; depth--) {
      const node = $from.node(depth);
      if (node.attrs?.['html-class'] !== undefined) {
        return node.attrs['html-class'];
      }
    }

    return '';
  }

  public static allHaveAny(state: EditorState): boolean {
    const { selection, doc } = state;
    const result: boolean[] = [];
    doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (doc.resolve(pos).depth === 0 && node.attrs['html-class'] !== undefined) {
        result.push(node.attrs['html-class'] !== '');
      }
    });
    return !!result.length && result.every(value => value);
  }

  public static anyCanHave(state: EditorState): boolean {
    const { selection, doc } = state;
    let result = false;
    doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (doc.resolve(pos).depth === 0 && node.attrs['html-class'] !== undefined) {
        result = true;
      }
    });
    return result;
  }

  public static apply(state: EditorState, value: string, dispatch?: PMDispatch): boolean {
    const { selection, tr } = state;

    // ✅ CASE 1: Columns node is fully selected
    if (
      selection instanceof NodeSelection &&
      selection.node.type.name === 'columns' &&
      selection.node.attrs['html-class'] !== undefined
    ) {
      tr.setNodeAttribute(selection.from, 'html-class', value);
      if (dispatch) dispatch(tr);
      return true;
    }

    // ✅ CASE 2: Apply to nearest node inside selection that supports 'html-class'
    const $from = selection.$from;
    for (let depth = $from.depth; depth >= 0; depth--) {
      const node = $from.node(depth);
      const pos = depth > 0 ? $from.before(depth) : 0;
      if (node.attrs?.['html-class'] !== undefined && node.type.name !== 'columns') {
        tr.setNodeAttribute(pos, 'html-class', value);
        if (dispatch) dispatch(tr);
        return true;
      }
    }

    return false;
  }

  public parseDOMGetAttrs(node: HTMLElement): HtmlClassAttrs {
    return {
      'html-class': node.getAttribute('class') || '',
    };
  }

  public getClassSerialization(node: ProsemirrorNode): string | null {
    const html_class = node.attrs['html-class'];
    if (html_class !== undefined && html_class !== HtmlClassExtension.attrs['html-class'].default) {
      return html_class;
    }
    return null;
  }
}
